import { chooseAppearanceSeed } from "./appearanceSeed";
/**
 * Application orchestrator: wires params, sim, renderer, joystick/scripted
 * input, HUD, and telemetry. The sim advances on a fixed timestep driven by
 * an accumulator inside the render loop.
 */

import { BUILD_STAMP } from "virtual:streetsense-build-stamp";
import profileJson from "../../config/robot_profile.json";
import measuredJson from "../../config/robot_profile_measured.json";
import { Joystick } from "../input/joystick";
import {
  getScriptedPlayer,
  type PlayerFn,
  type ScriptedPlayerDef,
} from "../players/scripted";
import { DebugOverlay } from "../render/debugOverlay";
import { Renderer, type PlayView } from "../render/scene";
import { appearanceTheme, DEFAULT_APPEARANCE_THEME, type AppearanceThemeId } from "../render/themes";
import { DT } from "../sim/constants";
import { MISSION_MAP_ID } from "../sim/mission";
import { resolveTuning } from "../sim/profile";
import { renderRoutedMap, type RoutedMap, type RoutedMapOptions } from "../sim/routed_map";
import { Simulation } from "../sim/simulation";
import type { MapDoc } from "../sim/map/types";
import type { ControlScheme, JoystickCommand, RobotProfile, Tuning } from "../sim/types";
import { NEUTRAL_COMMAND } from "../sim/types";
import { archiveEpisode, downloadEpisode } from "../telemetry/download";
import { uploadEpisode } from "../telemetry/upload";
import { buildEpisode, type RecorderContext } from "../telemetry/recorder";
import type { AppearanceRecord, EpisodeData } from "../telemetry/schema";
import { Hud } from "./hud";
import { captureSupported, safeName, saveCapture, ViewCapture } from "./capture";
import { PerfOverlay } from "./perfOverlay";
import {
  armWelcome,
  currentMapId,
  mapUrl,
  nextMapId,
  randomMapId,
  realMapsEnabled,
  type MapIndexEntry,
} from "./mapPicker";
import { buildSearch, nextLevel, parseParams, type AppParams } from "./params";
import {
  playAwkward,
  playDelivered,
  playDogYip,
  playHorn,
  playLaneStar,
  playResume,
  playSmooth,
  prepareAudio,
  setDrive,
  setMuted,
  startAmbience,
  unlockAudio,
} from "./sound";
import {
  loadSavedAppearanceTheme,
  saveAppearanceTheme,
  loadSavedEnv,
  loadSavedMuted,
  loadSavedSky,
  loadSavedWeather,
  loadSavedRobot,
  saveEnv,
  saveRobot,
  clearSavedScheme,
  clearSavedTuning,
  loadSavedScheme,
  loadSavedTuning,
  loadSavedView,
  saveMuted,
  saveSky,
  saveView,
  saveWeather,
  saveScheme,
  saveTuning,
} from "./tuning";

// v0.8.0: the street can be heard, and it is planted with real palms.
const APP_VERSION = "0.9.0";
/**
 * The map editor is an internal tool for now. Flip this off (or pass
 * ?editor=0) before the game goes in front of participants.
 */
const EDITOR_BUTTON = true;
/**
 * Longest single frame the sim will catch up on (s).
 *
 * 0.05, not 0.25. A hitch is going to happen — a garbage collection, a shader
 * compiled the first time something comes into view — and what the player
 * feels is not the dropped frame, it is the robot LEAPING when the sim runs
 * twelve steps in one go to catch up. At the arcade pace that was a metre and
 * a half of teleport; capped here it is about a third of a metre, roughly
 * twice a normal frame's movement and barely visible.
 *
 * The cost is the other side of the same trade, and it is the one this
 * codebase already chose: below ~20 fps the sim cannot keep up with real time
 * and degrades to slow motion rather than banking debt and fast-forwarding
 * when the frame rate recovers. The perf test holds the median at 30 fps.
 */
const MAX_FRAME_S = 0.05;
/** How long the mission shows its control reminder before the level hint. */
const HINT_SWAP_S = 7;
const CONTROL_HINT: Record<ControlScheme, string> = {
  rate: "Left / right steers · up to go, down to brake.",
  heading: "The stick points where you want to go.",
};
const MAX_STEPS_PER_FRAME = 3;

/**
 * Frames rendered before the sim starts stepping, and the frame time that
 * counts as ready (s).
 *
 * Babylon compiles a material's shader the first time it is drawn, so the
 * opening second of a fresh map lands a 400-500 ms freeze right as the player
 * starts driving. Rendering a few frames with the clock held pays that cost
 * before it can cost anything: the scene is on screen the whole time, and the
 * attempt simply starts when it is smooth. The frame budget is the real test;
 * the count is a floor so a fast device does not skip the warm-up entirely,
 * and the deadline is the escape hatch for a device that is never smooth.
 */
const WARMUP_MIN_FRAMES = 6;
/**
 * A frame at most this much slower than the best one seen counts as smooth.
 *
 * RELATIVE, not an absolute millisecond budget. An absolute 25 ms read as
 * "smooth" on a fast phone and as "never" on a weak one: a device drawing at
 * 18 fps has no 25 ms frames at all, so the run of smooth frames never
 * happened and the warm-up sat out its whole deadline — the player pressed
 * Drive and waited seconds. Measured against the device's own best frame, a
 * slow-but-steady machine settles as readily as a fast one, which is what the
 * warm-up is actually asking: has the expensive part passed?
 */
const WARMUP_SMOOTH_FACTOR = 1.6;
/** Floor for that comparison (s), so a freak fast frame cannot set a bar
 * nothing else can clear. */
const WARMUP_READY_S = 0.025;
/** Consecutive smooth frames required before the clock starts. */
const WARMUP_SMOOTH_RUN = 3;
/**
 * Hard deadline (s). Short on purpose: past this the game starts however
 * rough it is, because a player held at a still picture is worse than a
 * player driving through one hitch.
 */
const WARMUP_MAX_S = 1.2;

const PROFILE: RobotProfile = profileJson as RobotProfile;
/** The robot as measured from its own bags (ADR-074), behind ?dyn=measured. */
const PROFILE_MEASURED: RobotProfile = measuredJson as RobotProfile;

function uuid(): string {
  return typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `id-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

function sessionId(): string {
  try {
    let id = sessionStorage.getItem("streetsense_session");
    if (!id) {
      id = uuid();
      sessionStorage.setItem("streetsense_session", id);
    }
    return id;
  } catch {
    return uuid();
  }
}

export class Game {
  private variationSeed: number | null = null;
  readonly params: AppParams;
  private readonly renderer: Renderer;
  private readonly hud: Hud;
  private readonly joystick: Joystick;
  private readonly debug: DebugOverlay | null;
  private readonly scriptedDef: ScriptedPlayerDef | null;
  /** Recreated per attempt (stateful players must not leak state across retries). */
  private playerFn: PlayerFn | null = null;
  private readonly sessionId = sessionId();

  /** Source-change stamp of this build; shown in the HUD, asserted in e2e. */
  readonly buildStamp = BUILD_STAMP;
  sim!: Simulation;
  lastEpisode: EpisodeData | null = null;
  /** Tuning in force for the next attempt (saved -> query override -> defaults). */
  tuning: Tuning;
  /** Steering model for the next attempt, or null for the level's own default. */
  private scheme: ControlScheme | null;
  private attemptNumber = 0;
  /** The mission opens with a one-line control reminder, then the level hint. */
  private hintSwitched = true;
  private finalized = false;
  private accumulator = 0;
  private lastTime: number | null = null;
  /**
   * Held before the welcome card is dismissed. The attempt clock is real time
   * and the level times out, so a card in front of a running sim would eat the
   * player's budget while they read it.
   */
  private paused = false;
  /** Frames rendered since the attempt began, for the shader warm-up. */
  private warmupFrames = 0;
  private warmupStart = 0;
  private warmupSmooth = 0;
  /** Fastest frame seen this warm-up, the yardstick for "smooth". */
  private warmupBest = Infinity;
  /** `?perf=1` only: lets a device report its own frame timing (perfOverlay). */
  private readonly perf: PerfOverlay | null;
  /** The canvas, kept so a take can be started at any point in a run. */
  private readonly canvas: HTMLCanvasElement;
  /**
   * Recording state. Armed is a SESSION setting — it survives retries and the
   * map sequence — while `capture` is the single take in flight. Keeping them
   * apart is what makes "record the next five runs" work without the player
   * pressing anything again.
   */
  private recordArmed = false;
  private recordName = "";
  private capture: ViewCapture | null = null;
  /** The map list, fetched once, for the sequence and the random button. */
  private mapIndex: MapIndexEntry[] = [];

  constructor(
    canvas: HTMLCanvasElement,
    hudRoot: HTMLElement,
    joystickZone: HTMLElement,
    map?: MapDoc,
  ) {
    // A custom map is fetched and validated before the Game exists, so by
    // here it is known-good data and simply part of the scenario.
    this.params = { ...parseParams(window.location.search), ...(map ? { map } : {}) };
    this.canvas = canvas;
    // A map that brings its own reconstruction IS a photograph of a real
    // street, and the stylised themes have nothing to apply to it. Leaving the
    // saved theme in charge is how a visitor who once picked another one opens
    // `?map=pset028` and sees the gray-box world instead of the street —
    // reported from the deployed site, and the reason this exists. So a world
    // that declares photographs chooses the theme; an explicit `?theme=` in the
    // link still wins, because that is someone asking for the other thing.
    const wantsPhotographs = !!(this.params.splat ?? this.params.map?.splat?.url ?? this.params.pano);
    const themeId =
      this.params.appearanceTheme
      ?? (wantsPhotographs ? "realism" : null)
      ?? loadSavedAppearanceTheme()
      ?? DEFAULT_APPEARANCE_THEME;
    this.renderer = new Renderer(
      canvas,
      this.params.debug,
      this.params.view,
      this.params.optics,
      this.params.capture,
      this.params.gfx,
      themeId,
    );
    this.applyThemeInterface(themeId);
    if (this.params.pano && themeId === "realism") {
      // Same contract as a splat world: fire and forget, and never let a bad
      // URL take the session down.
      void this.renderer.loadPanoWorld(this.params.pano, { hideBuiltWorld: this.params.splatOnly });
    }
    // A map may carry its own reconstruction (ADR-098), and then `?map=<id>`
    // alone is the whole world — ground from the photograph, volume from the
    // gaussians — with `?splat=` left as an override for trying another. A map
    // that brings its own always hides the built scenery: it declared a real
    // street, not a gray-box one to decorate.
    const splatUrl = this.params.splat ?? this.params.map?.splat?.url ?? null;
    if (splatUrl && themeId === "realism") {
      // Fire-and-forget with its own error handling: a bad splat URL must
      // degrade to the ordinary world, never take the session down.
      // Say what is happening while it downloads. A reconstruction is tens of
      // megabytes and the built world is hidden from the first frame, so
      // without this the visitor watches an empty street for most of a minute
      // and concludes the map is broken (ADR-126).
      let lastShown = -1;
      void this.renderer
        .loadSplatWorld(splatUrl, {
          position: this.params.splatPos,
          rotationDeg: this.params.splatRotDeg,
          scale: this.params.splatScale,
          hideBuiltWorld: this.params.splatOnly || !!this.params.map?.splat,
          onProgress: (loaded, total) => {
            const mb = (n: number): string => (n / 1_000_000).toFixed(0);
            const pct = total > 0 ? Math.floor((loaded / total) * 100) : -1;
            // Repaint on whole percents only: this fires per network chunk.
            if (pct === lastShown) return;
            lastShown = pct;
            this.setHint(
              total > 0
                ? `Loading the real street — ${pct}% of ${mb(total)} MB`
                : `Loading the real street — ${mb(loaded)} MB so far`,
            );
          },
        })
        .then((ok) => {
          this.setHint(ok ? this.sim.level.hint : "The reconstruction could not be loaded.");
        });
    }
    // The machine and the world (ADR-048). A link pins either; otherwise the
    // saved choice; otherwise the real robot in the open district it works in.
    this.renderer.robotModel = this.params.robot ?? loadSavedRobot() ?? "bulldog";
    this.renderer.envTheme = this.params.env ?? loadSavedEnv() ?? "suburban";
    // A map that photographed its own sky flies that, not the theme's gradient
    // (ADR-133). Set before the first ambience pass so the dome never shows
    // the wrong sky and then swaps.
    const mapSky = this.params.map?.sky?.url ?? null;
    this.renderer.mapSkyUrl = mapSky ? `${import.meta.env.BASE_URL.replace(/\/$/, "")}${mapSky}` : null;
    this.renderer.skyChoice = loadSavedSky() ?? "auto";
    this.renderer.weatherChoice = loadSavedWeather() ?? "auto";
    this.joystick = new Joystick(joystickZone, this.params.stick);
    // Saved tuning is the baseline; an explicit ?sens/?spd in the URL wins
    // (reproducible links for researchers).
    this.tuning = resolveTuning({
      ...(loadSavedTuning() ?? {}),
      ...(this.params.tuningOverride ?? {}),
    });
    // ?steer= wins over the saved choice, so a link pins the model (ADR-023).
    this.scheme = this.params.controlScheme ?? loadSavedScheme();
    this.hud = new Hud(hudRoot, {
      onRetry: () => this.startAttempt(),
      onNext: () => this.gotoNext(),
      onDownload: () => this.download(),
      onTuningSave: (t, scheme, robot, env, themeId) => {
        this.prepareAppearanceChange();
        this.applyAppearance(themeId);
        this.tuning = resolveTuning(t);
        saveTuning(this.tuning);
        this.scheme = scheme;
        saveScheme(scheme);
        saveRobot(robot);
        saveEnv(env);
        this.renderer.robotModel = robot;
        this.renderer.envTheme = env;
        this.startAttempt(); // tuning is per-attempt: apply on a fresh episode
      },
      onOpenEditor: () => this.openEditor(),
      onNextMap: () => this.gotoMap(this.nextMapInSequence()),
      onRandomMap: () => {
        armWelcome(); // the card comes back on the map it lands on
        this.gotoMap(this.randomOtherMap());
      },
      onStart: () => this.resume(),
      onToggleView: () => this.setView(this.renderer.view === "first" ? "third" : "first"),
      onRecordArm: (armed, name) => this.setRecordArmed(armed, name),
      onAppearanceTheme: (id) => {
        if (id === this.renderer.appearance) return;
        this.prepareAppearanceChange();
        this.applyAppearance(id);
        this.startAttempt();
      },
      onRobotModel: (r) => {
        this.prepareAppearanceChange();
        saveRobot(r);
        this.renderer.robotModel = r;
        this.startAttempt();
      },
      onEnvTheme: (e) => {
        this.prepareAppearanceChange();
        saveEnv(e);
        this.renderer.envTheme = e;
        this.startAttempt();
      },
      onMute: (on) => {
        setMuted(on);
        saveMuted(on);
      },
      onSky: (v) => {
        this.prepareAppearanceChange();
        saveSky(v);
        this.renderer.skyChoice = v;
        this.startAttempt();
      },
      onWeather: (v) => {
        this.prepareAppearanceChange();
        saveWeather(v);
        this.renderer.weatherChoice = v;
        this.startAttempt();
      },
      onTuningReset: () => {
        clearSavedTuning();
        clearSavedScheme();
        this.scheme = this.params.controlScheme ?? null;
        this.tuning = resolveTuning(null);
        this.startAttempt();
      },
    });
    this.hud.setTuning(this.tuning);
    this.hud.setAppearanceTheme(themeId);
    this.hud.setRobot(this.renderer.robotModel);
    this.hud.setEnv(this.renderer.envTheme);
    this.hud.setMute(loadSavedMuted() ?? false);
    this.hud.setSky(this.renderer.skyChoice);
    this.hud.setWeather(this.renderer.weatherChoice);
    this.hud.setVersion(APP_VERSION, BUILD_STAMP);
    this.debug = this.params.debug
      ? new DebugOverlay(document.body, () => (this.sim.ended ? null : this.buildEpisodeNow()))
      : null;

    this.scriptedDef = this.params.player ? getScriptedPlayer(this.params.player) : null;
    if (this.scriptedDef) this.joystick.setVisible(false);
    // Internal tool: never on screen during scripted playback, so it cannot
    // turn up in a deliverable recording.
    this.hud.setEditorVisible(!this.scriptedDef && EDITOR_BUTTON && this.params.editorButton);
    // After the Hud, never before: its constructor assigns root.innerHTML,
    // which would wipe an element added to the same root ahead of it.
    this.perf = this.params.perf ? new PerfOverlay(hudRoot) : null;
    // The saved choice, so a player who prefers the driver's view keeps it
    // between maps and between sessions. A photographic world is the one place
    // that choice has a right answer: the chase camera sits 8 m back and 4.8 m
    // up, a viewpoint no camera ever occupied, so the reconstruction there is
    // guesswork and looks it. The driver's view is where the photographs were
    // taken. An explicit ?fpv= still wins.
    // A photographic world is convincing from where its cameras stood and falls
    // apart from anywhere else: a reconstruction has no data for the view from
    // 5 m up and behind, so the chase camera looks into the thin side of every
    // gaussian and the street reads as streaks. Start these worlds at the
    // robot's own eye height, which is where the photographs were taken.
    if (this.params.freeRoam) this.renderer.enableFreeRoam();
    const photographic = this.params.splatOnly || this.params.pano || !!this.params.map?.splat;
    this.setView(this.params.playView ?? (photographic ? "first" : loadSavedView()));

    // Only offered where it can actually work, and never during scripted
    // playback, which is how the deliverable recordings are made.
    this.hud.setRecordAvailable(!this.scriptedDef && captureSupported(canvas));
    const askedFor = this.params.record;
    this.hud.setRecordName(askedFor ?? "streetsense");
    // `?record=name` arms it from the URL, so a session can be set up by link
    // rather than by three taps on a phone before every run.
    if (askedFor !== null && askedFor !== undefined && captureSupported(canvas)) {
      this.setRecordArmed(true, askedFor);
      this.hud.setRecordArmedState(true);
    }

    this.hud.setMapsVisible(!this.scriptedDef && this.params.mapButton);
    this.hud.setMapControls(this.params.mapButton);

    // Audio needs a user gesture; the first joystick touch is the natural one.
    window.addEventListener(
      "pointerdown",
      () => {
        unlockAudio();
        startAmbience();
        setMuted(loadSavedMuted() ?? false);
      },
      { once: true },
    );

    this.startAttempt();
    this.renderer.start(() => this.frame());

    // Test/debug hook (read-only usage expected).
    (window as unknown as Record<string, unknown>)["__streetsense"] = this;
  }

  /** Finish the old visual condition before replacing its renderer state. */
  private prepareAppearanceChange(): void {
    if (this.sim && !this.finalized && this.sim.samples.length > 0) archiveEpisode(this.buildEpisodeNow());
    void this.endTake();
  }

  private applyThemeInterface(id: AppearanceThemeId): void {
    const root = document.documentElement;
    root.dataset.theme = id;
    for (const [key, value] of Object.entries(appearanceTheme(id).ui)) {
      root.style.setProperty("--theme-" + key, String(value));
    }
  }

  private applyAppearance(id: AppearanceThemeId): void {
    const previous = this.renderer.appearance;
    this.renderer.setAppearance(id);
    this.params.appearanceTheme = id;
    saveAppearanceTheme(id);
    this.applyThemeInterface(id);
    this.hud.setAppearanceTheme(id);
    const url = new URL(window.location.href);
    url.searchParams.set("theme", id);
    window.history.replaceState(null, "", url);
    if (id === "realism" && previous !== id && this.params.splat) {
      void this.renderer.loadSplatWorld(this.params.splat, {
        position: this.params.splatPos, rotationDeg: this.params.splatRotDeg, scale: this.params.splatScale,
      });
    }
  }

  /** Snapshot shared by episode exports and offline camera captures. */
  get appearance(): AppearanceRecord {
    return {
      ...this.renderer.appearanceManifest,
      robot_model: this.renderer.robotModel,
      world: this.renderer.envTheme,
      quality: this.renderer.qualityTier,
      configuration: JSON.parse(JSON.stringify(appearanceTheme(this.renderer.appearance))) as Record<string, unknown>,
    };
  }

  private setHint(text: string): void {
    const scriptedNote = this.params.player ? `  [SCRIPTED: ${this.params.player}]` : "";
    this.hud.setLevel(this.sim.level.name, text + scriptedNote);
  }

  /**
   * Open the map editor on whatever is being played. A custom map opens
   * itself, so the loop is play -> spot the problem -> fix it -> play, and
   * the built-in district opens as the map document generated from it.
   */
  private openEditor(): void {
    const map = this.params.mapId ?? MISSION_MAP_ID;
    const url = `${import.meta.env.BASE_URL}editor.html?map=${encodeURIComponent(map)}`;
    window.open(url, "_blank", "noopener");
  }

  /**
   * Show the welcome card and hold the sim behind it. The attempt clock is
   * real time and the level times out, so a card in front of a running sim
   * would eat the player's budget while they read it.
   */
  showWelcome(name: string, description: string): void {
    this.paused = true;
    this.joystick.setEnabled(false);
    this.hud.showStart(name, description);
  }

  /**
   * True while the renderer is still warming up: the scene draws, the sim
   * holds. Ends on the first cheap frame after WARMUP_MIN_FRAMES, or at the
   * deadline on a device that never gets there.
   */
  private warmingUp(now: number, rawDt: number): boolean {
    if (this.warmupFrames < 0) return false; // already done this attempt
    if (this.warmupStart === 0) this.warmupStart = now;
    this.warmupFrames++;

    // Consecutive smooth frames, measured on the RAW delta.
    //
    // This test used to take the delta the sim loop had already clamped to
    // MAX_FRAME_S, and compared it against the same number — so it was always
    // true and the warm-up only ever waited the frame floor. Worth stating
    // because it is invisible: the code reads as a smoothness check and was a
    // constant.
    //
    // A RUN of smooth frames rather than one, because the expensive frame is
    // usually followed by a cheap one and a single sample cannot tell "we are
    // through it" from "we are between two of them". Waiting on the symptom
    // rather than on shader readiness also covers whatever else is expensive
    // the first time — buffer uploads, the first garbage collection — instead
    // of only the cause we happen to have profiled.
    // Build the audio device here rather than on the first touch: it blocks
    // the main thread for ~260 ms, and on the touch it landed as a freeze on
    // the very frame the player started driving. Done on the second frame so
    // the first one paints promptly.
    if (this.warmupFrames === 2) prepareAudio();

    // The first frame's delta is measured from a lastTime that was never a
    // real frame, so it says nothing about this device.
    if (this.warmupFrames > 1) this.warmupBest = Math.min(this.warmupBest, rawDt);
    const budget = Math.max(WARMUP_READY_S, this.warmupBest * WARMUP_SMOOTH_FACTOR);
    this.warmupSmooth = rawDt <= budget ? this.warmupSmooth + 1 : 0;
    const settled =
      this.renderer.shadersReady &&
      this.warmupFrames >= WARMUP_MIN_FRAMES &&
      this.warmupSmooth >= WARMUP_SMOOTH_RUN;
    // The deadline is the escape hatch for a device that is never smooth: it
    // gets a playable game running behind real time rather than a hang.
    if (settled || now - this.warmupStart >= WARMUP_MAX_S) {
      this.warmupFrames = -1;
      return false;
    }
    return true;
  }

  private resume(): void {
    this.paused = false;
    this.lastTime = null;
    this.accumulator = 0;
    if (!this.sim.ended) this.joystick.setEnabled(true);
  }

  /** Give the HUD the map list so it can offer a sequence. */
  setMapIndex(entries: MapIndexEntry[]): void {
    this.mapIndex = entries;
    this.hud.setMapSequence(this.nextMapInSequence() !== null);
  }

  private nextMapInSequence(): string | null {
    return nextMapId(this.mapIndex, currentMapId(window.location.search),
                     realMapsEnabled(window.location.search));
  }

  private randomOtherMap(): string | null {
    return randomMapId(this.mapIndex, Math.random, currentMapId(window.location.search),
                       realMapsEnabled(window.location.search));
  }

  /** Navigate to another map, keeping the rest of the query. */
  private gotoMap(id: string | null): void {
    if (id === null) return;
    window.location.search = mapUrl(window.location.search, id);
  }

  private setView(view: PlayView): void {
    this.renderer.setPlayView(view);
    this.hud.setView(this.renderer.view);
    saveView(this.renderer.view);
  }

  private attemptKey(): string {
    const p = this.params;
    return `streetsense_att_${p.level}|${p.variant}|${p.mirror ? 1 : 0}|${p.seed}`;
  }

  private nextAttemptNumber(): number {
    let n = 1;
    try {
      n = Number(sessionStorage.getItem(this.attemptKey()) ?? "0") + 1;
      sessionStorage.setItem(this.attemptKey(), String(n));
    } catch {
      n = this.attemptNumber + 1;
    }
    return n;
  }

  startAttempt(): void {
    this.attemptNumber = this.nextAttemptNumber();
    this.finalized = false;
    this.lastEpisode = null;
    this.accumulator = 0;
    this.lastTime = null;
    this.warmupFrames = 0;
    this.warmupStart = 0;
    this.warmupSmooth = 0;
    this.warmupBest = Infinity;
    this.eventCursor = 0;
    this.honked.clear();
    // Fresh player per attempt: stateful players (e.g. `random`) must replay
    // identically on retry instead of continuing their previous stream.
    this.playerFn = this.scriptedDef ? this.scriptedDef.create(this.params.seed) : null;
    this.sim = new Simulation(
      { ...this.params, tuning: this.tuning, controlScheme: this.scheme ?? undefined },
      this.params.dyn === "measured" ? PROFILE_MEASURED : PROFILE,
      { attemptNumber: this.attemptNumber },
    );
    this.sim.freeRoam = this.params.freeRoam;
    this.hud.setTuning(this.sim.eff.tuning);
    this.hud.setScheme(this.sim.controlScheme, this.params.level === "mission");
    // Research and capture defaults are stable. Exploration changes only visuals.
    const controlled = Boolean(this.params.player || this.params.capture || this.params.level !== "mission"
      || (this.params.map ? this.params.map.research : true)
      || this.sim.level.mission?.encounters.some((encounter) => encounter.research));
    this.variationSeed = chooseAppearanceSeed(this.params.appearanceSeed, this.params.seed, controlled,
      this.variationSeed, () => crypto.getRandomValues(new Uint32Array(1))[0]!);
    this.renderer.setVariationSeed(this.variationSeed);
    this.renderer.buildLevel(this.sim);
    this.hud.hideEnd();
    this.joystick.setEnabled(true);
    // The mission changed control model (ADR-023), so say how to drive for
    // the first few seconds before handing the strip back to the level hint.
    const opening = this.sim.level.mission ? CONTROL_HINT[this.sim.controlScheme] : null;
    this.hintSwitched = opening === null;
    this.setHint(opening ?? this.sim.level.hint);
    const mission = this.sim.level.mission;
    if (mission) this.hud.buildMinimap(mission.network, mission.path, mission.destination);
    else this.hud.hideMinimap();
    this.beginTake();
  }

  /**
   * The route raster the real robot's driver sees, for the current instant
   * (ADR-075). Mission levels only — the lab levels have no road network.
   * Used by scripts/capture_clip.mjs through `window.__streetsense`.
   */
  routedMap(opts: RoutedMapOptions = {}): RoutedMap | null {
    const mission = this.sim.level.mission;
    if (!mission) return null;
    return renderRoutedMap(mission.network, mission.path, this.sim.robot, opts);
  }

  private laneAt = 0;
  /** Last speed/steering readout paint, ms (ADR-073). */
  private readoutAt = 0;
  /** The most recent stick command, for the motor voice (ADR-072). */
  private lastCmd: JoystickCommand = NEUTRAL_COMMAND;

  private frame(): void {
    const now = performance.now() / 1000;
    if (this.lastTime === null) this.lastTime = now;
    const rawDt = now - this.lastTime;
    const frameDt = Math.min(rawDt, MAX_FRAME_S);
    this.lastTime = now;

    // Evaluated BEFORE the `paused` test, not inside a `||` with it.
    //
    // `paused || warmingUp()` short-circuits, so while the welcome card was up
    // the warm-up never advanced — the whole of it (shader link, audio device,
    // the first expensive frames) was deferred until the player pressed "Drive
    // this map", landing exactly on the moment they started driving. Measured
    // at **520 ms between the press and the robot moving**, against 14 ms for
    // a map opened by direct link. That is the "giật ở đầu" that survived four
    // rounds of work, and it is why the built-in district never showed it: the
    // district is reached by picking it, never by the card.
    //
    // The scene renders behind the card, so this is the ideal place for the
    // warm-up to happen — the cost is paid while the player is reading.
    const warming = this.warmingUp(now, rawDt);
    if (this.paused || warming) {
      // Drop the elapsed time on the floor rather than banking it: starting
      // must not fast-forward through however long the wait was.
      this.accumulator = 0;
    } else if (!this.sim.ended) {
      this.accumulator += frameDt;
      let steps = 0;
      // Only the pose before the LAST step of the frame is the one the
      // renderer interpolates from; snapshotting on every step would do the
      // work up to three times and throw two of them away.
      const planned = Math.min(MAX_STEPS_PER_FRAME, Math.floor(this.accumulator / DT));
      while (this.accumulator >= DT && steps < MAX_STEPS_PER_FRAME && !this.sim.ended) {
        const cmd: JoystickCommand = this.playerFn
          ? this.playerFn(this.sim.view(), this.sim.t)
          : this.joystick.read();
        this.sim.setInput(cmd);
        // The ambience needs what the DRIVER asked for, not only what the
        // robot managed to do about it (ADR-072).
        this.lastCmd = cmd;
        // Keep the pose we are leaving, so the renderer can draw the world
        // BETWEEN steps instead of snapping from one to the next.
        if (steps === planned - 1) this.renderer.snapshot(this.sim);
        this.sim.step();
        this.accumulator -= DT;
        steps++;
      }
      // Cap leftover backlog: a sustained-slow device degrades to slow motion
      // instead of accumulating debt and fast-forwarding at up to 12x when the
      // frame rate recovers (which would silently break real-time play).
      if (this.accumulator > DT) this.accumulator = DT;
      if (!this.hintSwitched && this.sim.t > HINT_SWAP_S) {
        this.hintSwitched = true;
        this.setHint(this.sim.level.hint);
      }
      // Free-cam is a viewer, not an attempt. Letting the episode finish here
    // puts the delivery report over the middle of the thing being looked at and
    // stops the loop, which reads as "the world ends at the destination".
    if (this.sim.ended && !this.params.freeRoam) this.finalize();
    // Lane gauge at ~7 Hz: fast enough to feel live, cheap enough to ignore.
    const nowMs = performance.now();
    if (nowMs - this.laneAt > 150) {
      this.laneAt = nowMs;
      // No lane gauge in free roam: there is no lane to keep, and a bar that
      // scolds the driver for leaving one is the visible half of the fence
      // ADR-128 took down.
      this.hud.setLane(this.sim.ended || this.paused || this.params.freeRoam ? null : this.sim.lane);
    }
    }

    // Speed and steering, ~16 Hz (ADR-073). Faster than the lane gauge — a
    // needle stepping at 7 Hz reads as broken — and cheap: four text nodes
    // and two style writes, with CSS carrying the motion between ticks.
    const readoutNow = performance.now();
    if (readoutNow - this.readoutAt > 60) {
      this.readoutAt = readoutNow;
      const idle = this.sim.ended || this.paused;
      this.hud.setReadout(
        idle ? 0 : this.sim.robot.speed,
        idle ? 0 : this.sim.robot.yawRate,
        this.sim.eff.cruise_mps,
      );
    }

    // Motor and road noise, every frame (ADR-072). This used to ride along
    // with the lane gauge at ~7 Hz, which is a third of a stick movement late
    // — most of why the sound felt detached from the controls.
    {
      const idle = this.sim.ended || this.paused;
      const c = this.lastCmd;
      setDrive(
        idle ? 0 : this.sim.robot.speed,
        this.sim.eff.cruise_mps,
        idle ? 0 : c.mag,
        idle ? 0 : c.dirX * c.mag,
        !idle && c.dirY * c.mag < -0.15,
      );
    }

    // Mission HUD: distance-to-go, turn guidance, transient feedback
    // (text/sound/vibration), vehicle horns.
    if (this.sim.level.mission) {
      const view = this.sim.view();
      // The map is 76 px on screen; redrawing it every frame buys nothing.
      if ((this.minimapTick = (this.minimapTick + 1) % 4) === 0) {
        this.hud.updateMinimap(this.sim.robot.x, this.sim.robot.z, this.sim.robot.heading);
      }
      // Free roam has no errand, so it has no distance to go and nothing to be
      // guided towards: a counter ticking down to a destination the driver was
      // never sent to is the errand leaking into a session that exists to look
      // at the street (ADR-128).
      const errand = !this.sim.ended && !this.params.freeRoam;
      this.hud.setDistance(errand ? view.distanceToGo : null);
      this.hud.setGuidance(errand ? view.guidance : null, this.sim.cfg.mirror);
      this.processFeedbackEvents();
      this.processHorns();
    }

    // How far the clock stands between the last step and the next. The sim is
    // 60 Hz and a modern phone draws at 90 or 120, so without this the same
    // pose is drawn twice and the picture judders — see Renderer.update.
    this.renderer.update(this.sim, this.sim.ended ? 1 : Math.min(1, this.accumulator / DT));
    this.perf?.sample(
      this.renderer,
      !this.sim.ended && Math.abs(this.sim.robot.speed) > 0.05,
      !this.paused && this.warmupFrames < 0 && this.sim.t > 0,
    );
    this.debug?.update(this.sim);
  }

  private eventCursor = 0;
  private minimapTick = 0;
  private readonly honked = new Set<string>();

  /** A vehicle closing on the robot from behind warns once (cuePhase 4). */
  private processHorns(): void {
    for (const a of this.sim.agents) {
      const s = a.state;
      if (!s.active) continue;
      if (s.cuePhase === 4 && (s.type === "motorbike" || s.type === "motorcycle" || s.type === "car")) {
        if (!this.honked.has(s.id)) {
          this.honked.add(s.id);
          playHorn(s.type === "car");
        }
      }
    }
  }

  private processFeedbackEvents(): void {
    const events = this.sim.events;
    // Once the attempt has ended the end panel owns the screen: the last
    // encounter closes AT completion, and its feedback word would be drawn
    // across the score (both sit near 30% of the viewport height). Sounds
    // still play; only the floating text is suppressed.
    const silentText = this.sim.ended;
    const float = (text: string, tone: "smooth" | "awkward"): void => {
      if (!silentText) this.hud.showFloatingText(text, tone);
    };
    for (; this.eventCursor < events.length; this.eventCursor++) {
      const ev = events[this.eventCursor]!;
      if (ev.type === "wrong_way") {
        float("Wrong way", "awkward");
        playAwkward();
      } else if (ev.type === "feedback") {
        if (ev.data?.["kind"] === "smooth") {
          float("Smooth", "smooth");
          playSmooth();
        } else {
          float("Oops", "awkward");
          playAwkward();
        }
      } else if (ev.type === "encounter_start" && ev.data?.["encounter_type"] === "dog") {
        playDogYip();
      } else if (ev.type === "collision" || ev.type === "kerb_climb") {
        // The warning text is built in the sim so the wording matches the
        // report; the HUD only decides how loudly to show it.
        const w = this.sim.warning;
        if (w && !this.sim.ended) this.hud.showWarning(w.text, w.t);
        playAwkward();
      } else if (ev.type === "lane_star") {
        const combo = (ev.data?.["combo"] as number) ?? 1;
        const points = (ev.data?.["points"] as number) ?? 10;
        if (!silentText) this.hud.popStar(points, combo);
        playLaneStar(combo);
      } else if (ev.type === "resume_began" && ev.data?.["clean"] === 1) {
        // The corridor extended and the player took it after a clean wait.
        playResume();
      } else if (ev.type === "level_complete") {
        playDelivered();
      }
    }
  }

  private recorderContext(): RecorderContext {
    return {
      sessionId: this.sessionId,
      episodeId: uuid(),
      attemptNumber: this.attemptNumber,
      playerType: this.playerFn ? "scripted" : "human",
      scriptedPlayerName: this.params.player && this.playerFn ? this.params.player : null,
      device: {
        user_agent: navigator.userAgent,
        // Prefer the visual viewport (what the player actually sees once
        // mobile browser chrome collapses); fall back to the layout viewport.
        viewport_w: Math.round(window.visualViewport?.width ?? window.innerWidth),
        viewport_h: Math.round(window.visualViewport?.height ?? window.innerHeight),
        dpr: window.devicePixelRatio || 1,
      },
      createdAtIso: new Date().toISOString(),
      appVersion: APP_VERSION,
      appearance: this.appearance,
      playView: this.renderer.view,
      // The take this run belongs to. Written whether or not the upload
      // happens, so a run recorded on a phone with no signal still says which
      // video it goes with.
      videoBaseName: this.capture !== null || this.recordArmed ? this.takeName() : null,
    };
  }

  private finalize(): void {
    if (this.finalized) return;
    this.finalized = true;
    void this.endTake();
    this.lastEpisode = buildEpisode(this.sim, this.recorderContext());
    archiveEpisode(this.lastEpisode);
    // Never awaited: a slow or failed upload must not stand between the player
    // and their report (upload.ts). The panel just reports what happened.
    this.hud.setSaveState("saving");
    void uploadEpisode(this.lastEpisode).then((r) => {
      if (r.ok || r.reason === "already sent") {
        this.hud.setSaveState("saved");
      } else {
        console.warn("run not uploaded:", r.reason);
        this.hud.setSaveState("failed");
      }
    });
    const next = nextLevel(this.params.level);
    this.hud.setDistance(null);
    // The floating stick's touch band overlaps the end panel; hand the taps back.
    this.joystick.setEnabled(false);
    this.hud.showEnd(
      this.sim.outcome ?? this.lastEpisode.outcome,
      next !== null,
      this.sim.report,
      this.sim.level.mission !== undefined,
    );
  }

  /**
   * Arm or disarm recording.
   *
   * Arming mid-run starts a take from here rather than waiting for the next
   * attempt: someone who has just realised the run is worth keeping should
   * get the rest of it, not nothing.
   */
  private setRecordArmed(armed: boolean, name: string): void {
    this.recordArmed = armed;
    this.recordName = safeName(name);
    if (armed) {
      this.beginTake();
    } else {
      void this.endTake();
    }
  }

  /** Start a take, if recording is armed and one is not already running. */
  private beginTake(): void {
    if (!this.recordArmed || this.capture !== null) return;
    const c = new ViewCapture(this.canvas);
    const failed = c.start();
    if (failed !== null) {
      // Say so and disarm, rather than leaving a button lit that is doing
      // nothing. A player who thinks they are recording and is not has lost
      // the run twice over.
      console.warn("recording not started:", failed);
      this.recordArmed = false;
      this.hud.setRecordArmedState(false);
      this.hud.setRecordNote(`Could not record: ${failed}`);
      return;
    }
    this.capture = c;
    this.hud.setRecordNote(`Recording as ${this.takeName()}`);
  }

  /** Stop the take in flight and hand it to the browser as a download. */
  private async endTake(): Promise<void> {
    const c = this.capture;
    if (!c) return;
    this.capture = null;
    const base = this.takeName();
    const result = await c.stop();
    if (!result) {
      this.hud.setRecordNote("The recording came back empty.");
      return;
    }
    saveCapture(result.blob, base, result.mime);
    const mb = (result.blob.size / (1024 * 1024)).toFixed(1);
    this.hud.setRecordNote(`Saved ${base} — ${(result.ms / 1000).toFixed(0)} s, ${mb} MB`);
  }

  /**
   * The base name for this take.
   *
   * Map, view and attempt are in the name because a session produces a folder
   * full of these and "streetsense.webm (3)" tells nobody which run it was —
   * and the view matters most of all, since a first-person take and a chase
   * take of the same route are different footage for the same telemetry.
   */
  private takeName(): string {
    const map = currentMapId(window.location.search) ?? this.params.level;
    const view = this.renderer.view === "first" ? "fpv" : "chase";
    // Keep visual condition and attempt identity even for a 60-character user name.
    const suffix = safeName(`${this.renderer.appearance}-${this.attemptNumber}-${map}-${view}`);
    const prefix = this.recordName.slice(0, Math.max(0, 59 - suffix.length));
    return prefix ? `${prefix}-${suffix}` : suffix;
  }

  /** Build an episode snapshot of the current (possibly unfinished) attempt. */
  buildEpisodeNow(): EpisodeData {
    return buildEpisode(this.sim, this.recorderContext());
  }

  private download(): void {
    downloadEpisode(this.lastEpisode ?? this.buildEpisodeNow());
  }

  private gotoNext(): void {
    const next = nextLevel(this.params.level);
    if (!next) return;
    window.location.search = buildSearch(this.params, next);
  }
}
